import { useState, useCallback } from 'react';

/**
 * useLazyVisualizationフックのオプション
 */
export interface UseLazyVisualizationOptions {
  /**
   * 初期状態でレンダリングするかどうか
   * - true: 即座にレンダリング（小規模データ）
   * - false: プレースホルダー表示（大規模データ）
   */
  initialRender: boolean;

  /**
   * レンダリング開始時のコールバック
   */
  onRenderStart?: () => void;

  /**
   * レンダリング完了時のコールバック
   */
  onRenderComplete?: () => void;

  /**
   * レンダリングエラー時のコールバック
   */
  onRenderError?: (error: Error) => void;
}

/**
 * useLazyVisualizationフックの戻り値
 */
export interface UseLazyVisualizationResult {
  /**
   * 可視化をレンダリングすべきかどうか
   * - true: 可視化をレンダリング
   * - false: プレースホルダーを表示
   */
  shouldRender: boolean;

  /**
   * レンダリングを要求する関数
   * 一度呼ばれると、shouldRenderがtrueになり、その状態を保持
   */
  requestRender: () => void;

  /**
   * レンダリング処理中かどうか
   */
  isLoading: boolean;

  /**
   * レンダリングエラー情報
   */
  error: Error | null;

  /**
   * エラー後のリトライ関数
   */
  retry: () => void;

  /**
   * レンダリング完了を通知する関数（親コンポーネントから呼び出し）
   */
  notifyRenderComplete: () => void;

  /**
   * レンダリングエラーを通知する関数（親コンポーネントから呼び出し）
   */
  notifyRenderError: (error: Error) => void;
}

/**
 * 可視化コンポーネントの遅延レンダリング状態を管理するReactフック
 *
 * 大規模データセット（1,000行以上）の可視化をユーザーが明示的に要求するまで遅延させることで、
 * 初期表示のパフォーマンスを改善する。
 *
 * @example
 * ```tsx
 * function Histogram({ data }) {
 *   const dataSize = data.length;
 *   const {
 *     shouldRender,
 *     requestRender,
 *     isLoading,
 *     error,
 *     retry,
 *     notifyRenderComplete,
 *     notifyRenderError
 *   } = useLazyVisualization({
 *     initialRender: dataSize < 1000
 *   });
 *
 *   useEffect(() => {
 *     if (!shouldRender) return;
 *
 *     try {
 *       // D3レンダリング処理
 *       notifyRenderComplete();
 *     } catch (err) {
 *       notifyRenderError(err);
 *     }
 *   }, [shouldRender, data]);
 *
 *   if (!shouldRender || isLoading || error) {
 *     return <LazyVisualizationPlaceholder ... />;
 *   }
 *
 *   return <svg>...</svg>;
 * }
 * ```
 */
export function useLazyVisualization({
  initialRender,
  onRenderStart,
  onRenderComplete,
  onRenderError
}: UseLazyVisualizationOptions): UseLazyVisualizationResult {
  const [hasRequested, setHasRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 初期状態で表示、またはユーザーがリクエスト済みの場合はレンダリング
  const shouldRender = initialRender || hasRequested;

  const requestRender = useCallback(() => {
    if (isLoading) return; // 多重クリック防止

    setHasRequested(true);
    setIsLoading(true);
    setError(null);
    onRenderStart?.();
  }, [isLoading, onRenderStart]);

  const notifyRenderComplete = useCallback(() => {
    setIsLoading(false);
    onRenderComplete?.();
  }, [onRenderComplete]);

  const notifyRenderError = useCallback((err: Error) => {
    setIsLoading(false);
    setError(err);
    onRenderError?.(err);
  }, [onRenderError]);

  const retry = useCallback(() => {
    setError(null);
    requestRender();
  }, [requestRender]);

  return {
    shouldRender,
    requestRender,
    isLoading,
    error,
    retry,
    notifyRenderComplete,
    notifyRenderError
  };
}
